import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import CsvImportModal from "@/components/CsvImportModal";
import { toast } from "sonner";
import { Plus, X, Upload, Pencil, Save, Briefcase, UserCog } from "lucide-react";

const ROLE_LABEL = { employee: "Pracownik", manager: "Kierownik", office_land_admin: "Admin Office-Land" };
const EMPTY = {
  email: "", password: "", first_name: "", last_name: "", phone: "", role: "employee", site_id: "",
  sales_rep_name: "", sales_rep_phone: "", sales_rep_email: "",
  project_manager_name: "", project_manager_phone: "", project_manager_email: "",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [adding, setAdding] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    const u = await api.get("/users");
    setUsers(u.data);
    const s = await api.get("/sites");
    setSites(s.data);
  };

  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setForm(EMPTY);
    setEditingId(null);
    setAdding(true);
  };

  const startEdit = (u) => {
    setForm({
      email: u.email || "",
      password: "",
      first_name: u.first_name || "",
      last_name: u.last_name || "",
      phone: u.phone || "",
      role: u.role,
      site_id: u.site_id || "",
      sales_rep_name: u.sales_rep_name || "",
      sales_rep_phone: u.sales_rep_phone || "",
      sales_rep_email: u.sales_rep_email || "",
      project_manager_name: u.project_manager_name || "",
      project_manager_phone: u.project_manager_phone || "",
      project_manager_email: u.project_manager_email || "",
    });
    setEditingId(u.id);
    setAdding(true);
  };

  const closeForm = () => { setAdding(false); setEditingId(null); setForm(EMPTY); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      // Only send non-empty values to avoid wiping fields with empty strings
      const payload = {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
        role: form.role,
        site_id: form.site_id || null,
        phone: form.phone || null,
        sales_rep_name: form.sales_rep_name || null,
        sales_rep_phone: form.sales_rep_phone || null,
        sales_rep_email: form.sales_rep_email || null,
        project_manager_name: form.project_manager_name || null,
        project_manager_phone: form.project_manager_phone || null,
        project_manager_email: form.project_manager_email || null,
      };
      if (form.password) payload.password = form.password;

      if (editingId) {
        await api.patch(`/users/${editingId}`, payload);
        toast.success("Zapisano zmiany");
      } else {
        if (!form.password) {
          toast.error("Hasło jest wymagane dla nowego użytkownika");
          return;
        }
        await api.post("/users", payload);
        toast.success("Utworzono użytkownika");
      }
      closeForm();
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const showContactsSection = form.role === "employee" || form.role === "manager";
  const showProjectManagerSection = form.role === "manager";

  return (
    <div>
      <PageHeader testId="admin-users" title="Użytkownicy" subtitle={`${users.length} użytkowników`}
        actions={
          <>
            <button onClick={() => setImportOpen(true)} data-testid="import-users-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-300 hover:border-[#FF6B1A] hover:text-[#FF6B1A] text-sm font-semibold transition-colors">
              <Upload className="w-4 h-4" /> Importuj CSV
            </button>
            <button onClick={startCreate} data-testid="add-user-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold">
              <Plus className="w-4 h-4" /> Dodaj użytkownika
            </button>
          </>
        } />

      {adding && (
        <form onSubmit={submit} className="ol-card p-5 mb-4" data-testid="user-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              {editingId ? <Pencil className="w-4 h-4 text-[#FF6B1A]" /> : <Plus className="w-4 h-4 text-[#FF6B1A]" />}
              {editingId ? "Edytuj użytkownika" : "Nowy użytkownik"}
            </h3>
            <button type="button" onClick={closeForm} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Dane konta</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <input required placeholder="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email"
                className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
              <input placeholder={editingId ? "Nowe hasło (zostaw puste, by nie zmieniać)" : "Hasło *"} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="form-password"
                className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="form-role"
                className="px-3 py-2 rounded-md border border-slate-300 text-sm bg-white">
                <option value="employee">Pracownik</option>
                <option value="manager">Kierownik</option>
                <option value="office_land_admin">Admin Office-Land</option>
              </select>
              <input required placeholder="Imię *" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
              <input required placeholder="Nazwisko *" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
              <input type="tel" placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="form-phone"
                className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none" />
              <select value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} data-testid="form-site"
                className="md:col-span-3 px-3 py-2 rounded-md border border-slate-300 text-sm bg-white">
                <option value="">Bez budowy</option>
                {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>

          {showContactsSection && (
            <div className="mt-2 rounded-md border border-slate-200 bg-slate-50/60 p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5" /> Przedstawiciel handlowy Office-Land
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input placeholder="Imię i nazwisko" value={form.sales_rep_name} onChange={(e) => setForm({ ...form, sales_rep_name: e.target.value })} data-testid="form-sales-name"
                  className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none bg-white" />
                <input type="tel" placeholder="Telefon" value={form.sales_rep_phone} onChange={(e) => setForm({ ...form, sales_rep_phone: e.target.value })} data-testid="form-sales-phone"
                  className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none bg-white" />
                <input type="email" placeholder="Email" value={form.sales_rep_email} onChange={(e) => setForm({ ...form, sales_rep_email: e.target.value })} data-testid="form-sales-email"
                  className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none bg-white" />
              </div>
            </div>
          )}

          {showProjectManagerSection && (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50/60 p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5">
                <UserCog className="w-3.5 h-3.5" /> Project Manager Office-Land
                <span className="font-normal normal-case tracking-normal text-slate-400">— osoba zarządzająca całym projektem dla budów tego kierownika</span>
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input placeholder="Imię i nazwisko" value={form.project_manager_name} onChange={(e) => setForm({ ...form, project_manager_name: e.target.value })} data-testid="form-pm-name"
                  className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none bg-white" />
                <input type="tel" placeholder="Telefon" value={form.project_manager_phone} onChange={(e) => setForm({ ...form, project_manager_phone: e.target.value })} data-testid="form-pm-phone"
                  className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none bg-white" />
                <input type="email" placeholder="Email" value={form.project_manager_email} onChange={(e) => setForm({ ...form, project_manager_email: e.target.value })} data-testid="form-pm-email"
                  className="px-3 py-2 rounded-md border border-slate-300 text-sm focus:border-[#FF6B1A] focus:outline-none bg-white" />
              </div>
            </div>
          )}

          <div className="flex justify-end mt-4 gap-2">
            <button type="button" onClick={closeForm} className="px-4 py-2 rounded-md border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50">Anuluj</button>
            <button type="submit" data-testid="form-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#FF6B1A] hover:bg-[#E65A10] text-white text-sm font-semibold">
              <Save className="w-4 h-4" /> {editingId ? "Zapisz zmiany" : "Utwórz użytkownika"}
            </button>
          </div>
        </form>
      )}

      <div className="ol-card overflow-hidden">
        <table className="ol-table" data-testid="users-table">
          <thead>
            <tr><th>Email</th><th>Imię i nazwisko</th><th>Telefon</th><th>Rola</th><th>Budowa</th><th>Status</th><th></th></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const site = sites.find((s) => s.id === u.site_id);
              return (
                <tr key={u.id}>
                  <td className="font-mono text-xs">{u.email}</td>
                  <td>{u.first_name} {u.last_name}</td>
                  <td className="text-slate-600 font-mono text-xs">{u.phone || "—"}</td>
                  <td><span className="ol-badge bg-slate-100 text-slate-700 border border-slate-200">{ROLE_LABEL[u.role]}</span></td>
                  <td className="text-slate-600">{site?.name || "—"}</td>
                  <td>{u.active ? <span className="ol-badge bg-emerald-50 text-emerald-700 border border-emerald-200">Aktywny</span> : <span className="ol-badge bg-slate-100 text-slate-500">Nieaktywny</span>}</td>
                  <td className="space-x-3">
                    <button onClick={() => startEdit(u)} data-testid={`edit-user-${u.id}`}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-slate-700 hover:text-[#FF6B1A]">
                      <Pencil className="w-3.5 h-3.5" /> Edytuj
                    </button>
                    <button onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`}
                      className="text-xs font-semibold text-slate-600 hover:text-amber-700">
                      {u.active ? "Deaktywuj" : "Aktywuj"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {importOpen && (
        <CsvImportModal
          endpoint="/import/users"
          title="Importuj użytkowników z CSV"
          headers={["email", "password", "first_name", "last_name", "phone", "role", "site_name"]}
          sample={[
            ["jan.kowalski@ndi.pl", "Start2026!", "Jan", "Kowalski", "+48 600 100 200", "employee", "S6 Odcinek 2 Dołuje – Police"],
            ["ewa.nowak@ndi.pl", "Start2026!", "Ewa", "Nowak", "+48 600 300 400", "manager", "S6 Odcinek 3 Police – Goleniów"],
          ]}
          templateName="szablon_uzytkownicy.csv"
          onClose={() => setImportOpen(false)}
          onDone={load}
        />
      )}
    </div>
  );
}
